import { useCallback, useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';

import {
  isPromotionBannerInViewAtom,
  PROMOTION_DISPLAY_THRESHOLD_DAYS,
  useCatalogProductIdParams,
  usePdpBaseInfo,
} from '@domains/pdp-common';

const SECOND = 1000;
const MINUTE = SECOND * 60;
const HOUR = MINUTE * 60;
const DAY = HOUR * 24;

interface FloatingPromotionBannerServiceResult {
  shouldShow: boolean;
  countdown: string;
  priceChangeText: string;
  pointColor: string;
}

export const useFloatingPromotionBannerService = (): FloatingPromotionBannerServiceResult => {
  const params = useCatalogProductIdParams();
  const {
    product: { promotion_info },
  } = usePdpBaseInfo(params);

  const isPromotionBannerInView = useAtomValue(isPromotionBannerInViewAtom);
  const [, setTick] = useState(0);

  const floatBannerInfo = promotion_info?.float_banner_info;
  const timestamp = floatBannerInfo?.end_time ?? 0;

  // 1초마다 리렌더링을 위한 타이머
  useEffect(() => {
    if (!timestamp) {
      return;
    }

    const interval = setInterval(() => {
      setTick((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [timestamp]);

  const getTimeDiff = useCallback((): number => {
    if (!timestamp) {
      return 0;
    }
    const now = Date.now();
    // timestamp가 초 단위(10자리)면 밀리초로 변환, 이미 밀리초(13자리)면 그대로 사용
    const end = timestamp.toString().length <= 10 ? timestamp * 1000 : timestamp;
    return end - now;
  }, [timestamp]);

  const getCountdownText = useCallback((): string => {
    const diff = getTimeDiff();

    if (diff === 0) {
      return '';
    }

    // 잔여기간 종료
    if (diff <= 0) {
      return '종료';
    }

    const days = Math.floor(diff / DAY);

    // 잔여 기간 > PROMOTION_DISPLAY_THRESHOLD_DAYS일: 배너 미노출 (shouldShow에서 처리)
    if (days > PROMOTION_DISPLAY_THRESHOLD_DAYS) {
      return '';
    }

    // 1일 < 잔여기간 <= 14일: N일
    if (days >= 1) {
      return `${days}일`;
    }

    // 1일 >= 잔여기간: HH:MM:SS
    const hours = Math.floor(diff / HOUR);
    const minutes = Math.floor((diff % HOUR) / MINUTE);
    const seconds = Math.floor((diff % MINUTE) / SECOND);

    const hh = hours.toString().padStart(2, '0');
    const mm = minutes.toString().padStart(2, '0');
    const ss = seconds.toString().padStart(2, '0');

    return `${hh}:${mm}:${ss}`;
  }, [getTimeDiff]);

  const shouldShowPromotionLayout = Boolean(floatBannerInfo);

  // 표시 여부 결정: PROMOTION_DISPLAY_THRESHOLD_DAYS일 초과면 미노출, 종료는 노출
  const shouldShow = shouldShowPromotionLayout && !isPromotionBannerInView;

  // 색상 계산
  const pointColor = floatBannerInfo?.point_color?.normal ?? '';

  // 가격 변동 텍스트 조합 (예: "6,000 원 비싸져요")
  const priceChangeText = floatBannerInfo?.diff_amount_text ?? '';

  return {
    shouldShow,
    countdown: getCountdownText(),
    priceChangeText,
    pointColor,
  };
};
